import * as React from "react";
import { Download, FileText, Globe, Key, ShieldCheck, TestTube2 } from "lucide-react";
import { StatusBadge } from "./status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export interface EvidenceCheckItem {
  id: "files" | "tests" | "approvals" | "secrets" | "network" | string;
  name: string;
  detail: string;
  status: "verified" | "not-yet-due" | "unverified" | "missing";
}

export interface EvidenceChecklistProps extends React.HTMLAttributes<HTMLDivElement> {
  runId?: string;
  signatureStatus: "verified" | "unverified" | "missing" | "signed";
  items?: EvidenceCheckItem[];
  onDownload?: () => void;
  isDownloading?: boolean;
}

const checkIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  files: FileText,
  tests: TestTube2,
  approvals: ShieldCheck,
  secrets: Key,
  network: Globe,
};

/**
 * EvidenceChecklist displays the evidence checks for a run.
 * Only the item states the API returns are shown as verified; omitted items
 * are rendered as not verified, never as verified by default.
 */
export function EvidenceChecklist({
  runId: _runId,
  signatureStatus,
  items = [],
  onDownload,
  isDownloading = false,
  className,
  ...props
}: EvidenceChecklistProps) {
  const verifiedCount = items.filter((item) => item.status === "verified").length;
  const isSignatureVerified = signatureStatus === "verified" || signatureStatus === "signed";

  return (
    <Card className={cn("overflow-hidden", className)} {...props}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle className="text-base font-semibold">Evidence pack</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {verifiedCount} of {items.length} controls verified
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge
            kind="evidence"
            status={isSignatureVerified ? "verified" : signatureStatus}
            label={isSignatureVerified ? "Signature verified" : "Unverified"}
          />
          {onDownload ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDownload}
              disabled={isDownloading}
              className="gap-1.5"
            >
              <Download aria-hidden className="size-3.5" />
              {isDownloading ? "Downloading…" : "Download"}
            </Button>
          ) : null}
        </div>
      </CardHeader>

      <CardContent>
        <ul className="flex flex-col divide-y divide-border/60 text-sm">
          {items.map((item) => {
            const Icon = checkIcons[item.id] ?? FileText;
            const isOk = item.status === "verified";
            const isMissing = item.status === "missing";

            return (
              <li
                key={item.id}
                className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="flex items-start gap-3">
                  <span
                    className={cn(
                      "mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border text-xs",
                      isOk
                        ? "border-success/35 bg-success/10 text-success"
                        : isMissing
                          ? "border-danger/35 bg-danger/10 text-danger"
                          : "border-border bg-muted text-muted-foreground",
                    )}
                  >
                    <Icon className="size-3.5" />
                  </span>
                  <div className="flex flex-col gap-0.5">
                    <p className="font-medium text-foreground">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.detail}</p>
                  </div>
                </div>

                <StatusBadge kind="evidence" status={item.status} />
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
